import { Maximize2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type AssistantDifficulty = 'easy' | 'medium' | 'hard';
export type AssistantArcadeGame = 'flappy' | 'block' | 'dino' | 'snake';

type AssistantCanvasGamesProps = {
  game: AssistantArcadeGame;
  difficulty: AssistantDifficulty;
  onRequestFullscreen?: () => void;
  showFullscreenButton?: boolean;
  canvasHeight?: number;
  className?: string;
};

type Size = { width: number; height: number };

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const useElementSize = <T extends HTMLElement>() => {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const update = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: Math.round(rect.width), height: Math.round(rect.height) });
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return { ref, size };
};

const draw3DRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  depth = 6,
) => {
  const sideColor = 'rgba(0,0,0,0.18)';
  const topColor = 'rgba(255,255,255,0.18)';

  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);

  ctx.fillStyle = sideColor;
  ctx.beginPath();
  ctx.moveTo(x + w, y);
  ctx.lineTo(x + w + depth, y - depth);
  ctx.lineTo(x + w + depth, y + h - depth);
  ctx.lineTo(x + w, y + h);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = topColor;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w + depth, y - depth);
  ctx.lineTo(x + depth, y - depth);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(0,0,0,0.22)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);
};

const drawGround = (ctx: CanvasRenderingContext2D, width: number, height: number, groundY: number) => {
  const grad = ctx.createLinearGradient(0, groundY, 0, height);
  grad.addColorStop(0, '#6bbf45');
  grad.addColorStop(0.1, '#4f9e2d');
  grad.addColorStop(0.11, '#c56a20');
  grad.addColorStop(1, '#8a470e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, groundY, width, height - groundY);

  ctx.strokeStyle = 'rgba(0,0,0,0.12)';
  ctx.lineWidth = 1;
  for (let y = groundY + 12; y < height; y += 12) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }
};

const drawSky = (ctx: CanvasRenderingContext2D, width: number, height: number, mode: 'day' | 'night') => {
  const grad = ctx.createLinearGradient(0, 0, 0, height);
  if (mode === 'night') {
    grad.addColorStop(0, '#07122f');
    grad.addColorStop(1, '#140a2a');
  } else {
    grad.addColorStop(0, '#87CEEB');
    grad.addColorStop(1, '#e7f7ff');
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);
};

type Engine = {
  start: () => void;
  stop: () => void;
  jump: () => void;
  getScore: () => number;
  isRunning: () => boolean;
  handleKeyDown?: (event: KeyboardEvent) => boolean;
};

const createFlappyEngine = (
  canvas: HTMLCanvasElement,
  difficulty: AssistantDifficulty,
  onScore: (score: number) => void,
): Engine => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { start: () => undefined, stop: () => undefined, jump: () => undefined, getScore: () => 0, isRunning: () => false };
  }

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;
  const groundY = height - 28;

  const params =
    difficulty === 'easy'
      ? { speed: 2.2, gravity: 0.32, flap: -6.4, gap: 120 }
      : difficulty === 'hard'
        ? { speed: 3.4, gravity: 0.42, flap: -7.2, gap: 92 }
        : { speed: 2.8, gravity: 0.38, flap: -6.8, gap: 104 };

  let rafId = 0;
  let running = false;
  let score = 0;
  let frame = 0;
  let alive = true;
  let bird = { x: width * 0.22, y: height * 0.45, vy: 0, r: 12 };
  let pipes: Array<{ x: number; gapY: number; scored: boolean }> = [];

  const spawnPipe = () => {
    const margin = 44;
    const usable = groundY - margin * 2 - params.gap;
    const gapY = margin + Math.random() * Math.max(40, usable);
    pipes.push({ x: width + 12, gapY, scored: false });
  };

  const reset = () => {
    score = 0;
    frame = 0;
    alive = true;
    bird = { x: width * 0.22, y: height * 0.45, vy: 0, r: 12 };
    pipes = [];
    spawnPipe();
    onScore(score);
  };

  const jump = () => {
    if (!running) return;
    if (!alive) {
      reset();
      return;
    }
    bird.vy = params.flap;
  };

  const drawBird = () => {
    ctx.save();
    ctx.translate(bird.x, bird.y);
    const tilt = clamp(bird.vy * 0.06, -0.6, 0.6);
    ctx.rotate(tilt);

    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.beginPath();
    ctx.ellipse(3, bird.r + 5, bird.r * 0.9, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    const grad = ctx.createRadialGradient(-bird.r * 0.3, -bird.r * 0.3, bird.r * 0.2, 0, 0, bird.r);
    grad.addColorStop(0, '#ffd480');
    grad.addColorStop(0.55, '#ff8c00');
    grad.addColorStop(1, '#cc5500');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(0, 0, bird.r, bird.r * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.beginPath();
    ctx.ellipse(-bird.r * 0.25, -bird.r * 0.25, bird.r * 0.45, bird.r * 0.28, -0.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(bird.r * 0.35, -bird.r * 0.2, bird.r * 0.32, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#111';
    ctx.beginPath();
    ctx.arc(bird.r * 0.44, -bird.r * 0.2, bird.r * 0.16, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  };

  const drawPipes = () => {
    const pipeW = 38;
    pipes.forEach((pipe) => {
      const topH = pipe.gapY;
      const botY = pipe.gapY + params.gap;
      const botH = Math.max(10, groundY - botY);
      draw3DRect(ctx, pipe.x, 0, pipeW, topH, '#2f9c22', 7);
      draw3DRect(ctx, pipe.x, botY, pipeW, botH, '#2f9c22', 7);
    });
  };

  const drawHud = () => {
    ctx.save();
    ctx.fillStyle = 'rgba(255,107,0,0.92)';
    ctx.font = "800 14px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = 'center';
    ctx.fillText(`Score: ${score}`, width / 2, 22);
    ctx.restore();
  };

  const drawGameOver = () => {
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.font = "900 20px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.fillText('Game Over', width / 2, height / 2 - 12);
    ctx.font = "700 12px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillText('Click / Space to restart', width / 2, height / 2 + 12);
    ctx.restore();
  };

  const step = () => {
    if (!running) return;
    frame += 1;
    bird.vy += params.gravity;
    bird.y += bird.vy;

    if (frame % 92 === 0) spawnPipe();
    pipes.forEach((pipe) => (pipe.x -= params.speed));
    pipes = pipes.filter((pipe) => pipe.x > -80);

    pipes.forEach((pipe) => {
      if (!pipe.scored && pipe.x + 38 < bird.x) {
        pipe.scored = true;
        score += 1;
        onScore(score);
      }
    });

    if (bird.y - bird.r < 0 || bird.y + bird.r > groundY) {
      alive = false;
    }

    pipes.forEach((pipe) => {
      const pipeW = 38;
      const inX = bird.x + bird.r > pipe.x + 3 && bird.x - bird.r < pipe.x + pipeW - 3;
      const hitTop = bird.y - bird.r < pipe.gapY;
      const hitBottom = bird.y + bird.r > pipe.gapY + params.gap;
      if (inX && (hitTop || hitBottom)) alive = false;
    });

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawSky(ctx, width, height, 'day');
    drawGround(ctx, width, height, groundY);
    drawPipes();
    drawBird();
    drawHud();
    if (!alive) drawGameOver();

    rafId = window.requestAnimationFrame(step);
  };

  return {
    start: () => {
      if (running) return;
      running = true;
      reset();
      rafId = window.requestAnimationFrame(step);
    },
    stop: () => {
      running = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = 0;
    },
    jump,
    getScore: () => score,
    isRunning: () => running,
  };
};

// Block runner (Minecraft-like blocks) and Dino (pixel runner) share runner mechanics but use different rendering.
const createBlockRunnerEngine = (
  canvas: HTMLCanvasElement,
  difficulty: AssistantDifficulty,
  onScore: (score: number) => void,
): Engine => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { start: () => undefined, stop: () => undefined, jump: () => undefined, getScore: () => 0, isRunning: () => false };
  }

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;
  const groundY = height - 30;

  const params =
    difficulty === 'easy'
      ? { speed: 2.35, gravity: 0.44, jump: -8.9, spawnEvery: 96 }
      : difficulty === 'hard'
        ? { speed: 3.65, gravity: 0.54, jump: -9.9, spawnEvery: 70 }
        : { speed: 3.05, gravity: 0.49, jump: -9.3, spawnEvery: 82 };

  let rafId = 0;
  let running = false;
  let score = 0;
  let frame = 0;
  let alive = true;
  let player = { x: width * 0.14, y: groundY, vy: 0, w: 22, h: 30, onGround: true };
  let obstacles: Array<{ x: number; w: number; h: number; kind: 'brick' | 'pipe' | 'spike' }> = [];
  let coins: Array<{ x: number; y: number; r: number; taken: boolean }> = [];

  const reset = () => {
    score = 0;
    frame = 0;
    alive = true;
    player = { x: width * 0.14, y: groundY, vy: 0, w: 22, h: 30, onGround: true };
    obstacles = [];
    coins = [];
    onScore(score);
  };

  const jump = () => {
    if (!running) return;
    if (!alive) {
      reset();
      return;
    }
    if (!player.onGround) return;
    player.vy = params.jump;
    player.onGround = false;
  };

  const spawnObstacle = () => {
    const kind = Math.random() < 0.55 ? 'brick' : Math.random() < 0.6 ? 'pipe' : 'spike';
    const base = kind === 'pipe' ? 28 : kind === 'spike' ? 22 : 20;
    const size = base + Math.random() * 18;
    obstacles.push({ x: width + 18, w: Math.max(16, size * 0.78), h: size, kind });
  };

  const spawnCoin = () => {
    const r = 6;
    const y = groundY - (44 + Math.random() * 24);
    coins.push({ x: width + 50 + Math.random() * 60, y, r, taken: false });
  };

  const drawPlayer = () => {
    const topY = player.y - player.h;
    draw3DRect(ctx, player.x, topY, player.w, player.h, '#ff6b00', 7);
    draw3DRect(ctx, player.x + 5, topY + 6, 6, 9, 'rgba(255,255,255,0.2)', 3);
    draw3DRect(ctx, player.x + 4, topY - 8, player.w - 6, 8, '#2d1800', 4);
  };

  const drawObstacles = () => {
    obstacles.forEach((o) => {
      const y = groundY - o.h;
      if (o.kind === 'brick') {
        draw3DRect(ctx, o.x, y, o.w, o.h, '#c56a20', 8);
        ctx.strokeStyle = 'rgba(0,0,0,0.15)';
        for (let row = 0; row < 3; row += 1) {
          const yy = y + (o.h / 3) * row;
          ctx.beginPath();
          ctx.moveTo(o.x, yy);
          ctx.lineTo(o.x + o.w, yy);
          ctx.stroke();
        }
      } else if (o.kind === 'pipe') {
        draw3DRect(ctx, o.x, y, o.w, o.h, '#2f9c22', 8);
        draw3DRect(ctx, o.x - 4, y - 10, o.w + 8, 10, '#3ab42b', 8);
      } else {
        draw3DRect(ctx, o.x, y, o.w, o.h, '#8b4513', 7);
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.beginPath();
        ctx.moveTo(o.x + 3, groundY);
        ctx.lineTo(o.x + o.w / 2, y + 4);
        ctx.lineTo(o.x + o.w - 3, groundY);
        ctx.closePath();
        ctx.fill();
      }
    });
  };

  const drawCoins = () => {
    coins.forEach((c) => {
      if (c.taken) return;
      const grad = ctx.createRadialGradient(c.x - c.r * 0.3, c.y - c.r * 0.3, 2, c.x, c.y, c.r);
      grad.addColorStop(0, '#ffe28a');
      grad.addColorStop(1, '#cc9900');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.25)';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  };

  const drawHud = (modeLabel: string) => {
    ctx.save();
    ctx.fillStyle = 'rgba(255,107,0,0.92)';
    ctx.font = "800 13px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = 'left';
    ctx.fillText(modeLabel, 10, 20);
    ctx.textAlign = 'right';
    ctx.fillText(`Score: ${score}`, width - 10, 20);
    ctx.restore();
  };

  const drawGameOver = () => {
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.font = "900 20px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.fillText('Game Over', width / 2, height / 2 - 12);
    ctx.font = "700 12px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillText('Click / Space to restart', width / 2, height / 2 + 12);
    ctx.restore();
  };

  const step = () => {
    if (!running) return;
    frame += 1;

    player.vy += params.gravity;
    player.y += player.vy;
    if (player.y >= groundY) {
      player.y = groundY;
      player.vy = 0;
      player.onGround = true;
    }

    if (frame % params.spawnEvery === 0) spawnObstacle();
    if (frame % Math.max(52, Math.floor(params.spawnEvery * 0.8)) === 0) spawnCoin();
    obstacles.forEach((o) => (o.x -= params.speed));
    obstacles = obstacles.filter((o) => o.x > -80);
    coins.forEach((c) => (c.x -= params.speed));
    coins = coins.filter((c) => c.x > -40);

    if (frame % 10 === 0) {
      score += 1;
      onScore(score);
    }

    // Coin pickups
    coins.forEach((c) => {
      if (c.taken) return;
      const px = player.x + player.w / 2;
      const py = player.y - player.h / 2;
      const dx = px - c.x;
      const dy = py - c.y;
      if (dx * dx + dy * dy < (c.r + 10) * (c.r + 10)) {
        c.taken = true;
        score += 6;
        onScore(score);
      }
    });

    // Collision
    const px1 = player.x + 2;
    const px2 = player.x + player.w - 2;
    const py1 = player.y - player.h + 2;
    const py2 = player.y - 2;
    obstacles.forEach((o) => {
      const ox1 = o.x + 2;
      const ox2 = o.x + o.w - 2;
      const oy1 = groundY - o.h + 2;
      const oy2 = groundY - 2;
      const hit = px1 < ox2 && px2 > ox1 && py1 < oy2 && py2 > oy1;
      if (hit) alive = false;
    });

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawSky(ctx, width, height, 'day');
    drawGround(ctx, width, height, groundY);
    drawObstacles();
    drawCoins();
    drawPlayer();
    drawHud('Block Jump');
    if (!alive) drawGameOver();

    rafId = window.requestAnimationFrame(step);
  };

  return {
    start: () => {
      if (running) return;
      running = true;
      reset();
      rafId = window.requestAnimationFrame(step);
    },
    stop: () => {
      running = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = 0;
    },
    jump,
    getScore: () => score,
    isRunning: () => running,
  };
};

const createDinoPixelEngine = (
  canvas: HTMLCanvasElement,
  difficulty: AssistantDifficulty,
  onScore: (score: number) => void,
): Engine => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { start: () => undefined, stop: () => undefined, jump: () => undefined, getScore: () => 0, isRunning: () => false };
  }

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;
  const groundY = height - 28;

  const params =
    difficulty === 'easy'
      ? { speed: 2.6, gravity: 0.5, jump: -10.2, spawnEvery: 92 }
      : difficulty === 'hard'
        ? { speed: 3.95, gravity: 0.62, jump: -11.2, spawnEvery: 68 }
        : { speed: 3.25, gravity: 0.56, jump: -10.7, spawnEvery: 78 };

  const pixelScale = 3;
  const pxWidth = Math.max(1, Math.floor(width / pixelScale));
  const pxHeight = Math.max(1, Math.floor(height / pixelScale));
  const pxCanvas = document.createElement('canvas');
  pxCanvas.width = pxWidth;
  pxCanvas.height = pxHeight;
  const pxCtx = pxCanvas.getContext('2d');
  if (!pxCtx) {
    return { start: () => undefined, stop: () => undefined, jump: () => undefined, getScore: () => 0, isRunning: () => false };
  }

  let rafId = 0;
  let running = false;
  let score = 0;
  let frame = 0;
  let alive = true;
  let dino = { x: Math.floor(pxWidth * 0.15), y: Math.floor(groundY / pixelScale), vy: 0, w: 10, h: 10, onGround: true };
  let obstacles: Array<{ x: number; w: number; h: number }> = [];
  let phase = 0;

  const reset = () => {
    score = 0;
    frame = 0;
    alive = true;
    dino = { x: Math.floor(pxWidth * 0.15), y: Math.floor(groundY / pixelScale), vy: 0, w: 10, h: 10, onGround: true };
    obstacles = [];
    onScore(score);
  };

  const jump = () => {
    if (!running) return;
    if (!alive) {
      reset();
      return;
    }
    if (!dino.onGround) return;
    dino.vy = params.jump / pixelScale;
    dino.onGround = false;
  };

  const spawnObstacle = () => {
    const h = 6 + Math.floor(Math.random() * 6);
    const w = 4 + Math.floor(Math.random() * 4);
    obstacles.push({ x: pxWidth + 4, w, h });
  };

  const drawPixelScene = () => {
    const isNight = phase > 520;
    pxCtx.imageSmoothingEnabled = false;
    const sky = pxCtx.createLinearGradient(0, 0, 0, pxHeight);
    if (isNight) {
      sky.addColorStop(0, '#0a173a');
      sky.addColorStop(1, '#1b0f2f');
    } else {
      sky.addColorStop(0, '#74c8ff');
      sky.addColorStop(1, '#fff2c7');
    }
    pxCtx.fillStyle = sky;
    pxCtx.fillRect(0, 0, pxWidth, pxHeight);

    // Ground
    const gY = Math.floor(groundY / pixelScale);
    pxCtx.fillStyle = isNight ? '#3c7a2c' : '#5cbf42';
    pxCtx.fillRect(0, gY, pxWidth, pxHeight - gY);
    pxCtx.fillStyle = isNight ? '#8b4c1e' : '#c56a20';
    pxCtx.fillRect(0, gY + 2, pxWidth, pxHeight - (gY + 2));

    // Obstacles (cacti)
    obstacles.forEach((o) => {
      const y = gY - o.h;
      pxCtx.fillStyle = isNight ? '#2eea8b' : '#12b981';
      pxCtx.fillRect(o.x, y, o.w, o.h);
      pxCtx.fillStyle = 'rgba(255,255,255,0.25)';
      pxCtx.fillRect(o.x + 1, y + 1, 1, Math.max(1, o.h - 2));
    });

    // Dino (pixel body)
    const dx = Math.floor(dino.x);
    const dy = Math.floor(dino.y - dino.h);
    pxCtx.fillStyle = '#ff6b00';
    pxCtx.fillRect(dx, dy, dino.w, dino.h);
    pxCtx.fillStyle = '#2d1800';
    pxCtx.fillRect(dx + 2, dy - 3, 6, 3);
    pxCtx.fillStyle = '#fff';
    pxCtx.fillRect(dx + 6, dy + 2, 2, 2);

    // HUD
    pxCtx.fillStyle = isNight ? 'rgba(255,255,255,0.92)' : 'rgba(20,20,20,0.75)';
    pxCtx.font = "bold 6px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    pxCtx.fillText(`S:${score}`, 2, 8);
  };

  const step = () => {
    if (!running) return;
    frame += 1;
    phase = (phase + 1) % 900;

    const gY = Math.floor(groundY / pixelScale);
    dino.vy += params.gravity / (pixelScale * 1.15);
    dino.y += dino.vy;
    if (dino.y >= gY) {
      dino.y = gY;
      dino.vy = 0;
      dino.onGround = true;
    }

    if (frame % params.spawnEvery === 0) spawnObstacle();
    obstacles.forEach((o) => (o.x -= params.speed / pixelScale));
    obstacles = obstacles.filter((o) => o.x > -20);

    if (frame % 10 === 0) {
      score += 1;
      onScore(score);
    }

    // Collision (pixel bounds)
    const px1 = dino.x + 1;
    const px2 = dino.x + dino.w - 1;
    const py1 = dino.y - dino.h + 1;
    const py2 = dino.y - 1;
    obstacles.forEach((o) => {
      const ox1 = o.x + 1;
      const ox2 = o.x + o.w - 1;
      const oy1 = gY - o.h + 1;
      const oy2 = gY - 1;
      const hit = px1 < ox2 && px2 > ox1 && py1 < oy2 && py2 > oy1;
      if (hit) alive = false;
    });

    drawPixelScene();

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(pxCanvas, 0, 0, pxWidth, pxHeight, 0, 0, width, height);
    if (!alive) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.font = "900 18px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText('Game Over', width / 2, height / 2 - 10);
      ctx.font = "700 12px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText('Click / Space to restart', width / 2, height / 2 + 12);
    }

    rafId = window.requestAnimationFrame(step);
  };

  return {
    start: () => {
      if (running) return;
      running = true;
      reset();
      rafId = window.requestAnimationFrame(step);
    },
    stop: () => {
      running = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = 0;
    },
    jump,
    getScore: () => score,
    isRunning: () => running,
  };
};

const createSnakeEngine = (
  canvas: HTMLCanvasElement,
  difficulty: AssistantDifficulty,
  onScore: (score: number) => void,
): Engine => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { start: () => undefined, stop: () => undefined, jump: () => undefined, getScore: () => 0, isRunning: () => false };
  }

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;

  const params =
    difficulty === 'easy'
      ? { tickMs: 130, cols: 18, rows: 12 }
      : difficulty === 'hard'
        ? { tickMs: 75, cols: 22, rows: 14 }
        : { tickMs: 95, cols: 20, rows: 13 };

  let rafId = 0;
  let running = false;
  let alive = true;
  let score = 0;
  let lastTs = 0;
  let acc = 0;

  let dir = { x: 1, y: 0 };
  let nextDir = { x: 1, y: 0 };
  let snake: Array<{ x: number; y: number }> = [];
  let food = { x: 0, y: 0 };

  const cell = Math.max(10, Math.floor(Math.min(width / params.cols, height / params.rows)));
  const boardW = params.cols * cell;
  const boardH = params.rows * cell;
  const ox = Math.floor((width - boardW) / 2);
  const oy = Math.floor((height - boardH) / 2);

  const randomEmptyCell = () => {
    const occupied = new Set(snake.map((p) => `${p.x},${p.y}`));
    for (let tries = 0; tries < 500; tries += 1) {
      const x = Math.floor(Math.random() * params.cols);
      const y = Math.floor(Math.random() * params.rows);
      if (!occupied.has(`${x},${y}`)) return { x, y };
    }
    return { x: 0, y: 0 };
  };

  const reset = () => {
    alive = true;
    score = 0;
    dir = { x: 1, y: 0 };
    nextDir = { x: 1, y: 0 };
    const startX = Math.floor(params.cols / 2);
    const startY = Math.floor(params.rows / 2);
    snake = [
      { x: startX - 1, y: startY },
      { x: startX, y: startY },
      { x: startX + 1, y: startY },
    ];
    food = randomEmptyCell();
    onScore(score);
  };

  const tick = () => {
    if (!alive) return;
    dir = nextDir;
    const head = snake[snake.length - 1];
    const next = { x: head.x + dir.x, y: head.y + dir.y };

    if (next.x < 0 || next.y < 0 || next.x >= params.cols || next.y >= params.rows) {
      alive = false;
      return;
    }

    const willEat = next.x === food.x && next.y === food.y;
    const bodyToCheck = willEat ? snake : snake.slice(1);
    const hitSelf = bodyToCheck.some((p) => p.x === next.x && p.y === next.y);
    if (hitSelf) {
      alive = false;
      return;
    }

    snake.push(next);
    if (willEat) {
      score += 1;
      onScore(score);
      food = randomEmptyCell();
    } else {
      snake.shift();
    }
  };

  const draw = () => {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    // Board background (green grid)
    ctx.fillStyle = '#2f6a2f';
    ctx.fillRect(ox - 10, oy - 10, boardW + 20, boardH + 20);
    for (let y = 0; y < params.rows; y += 1) {
      for (let x = 0; x < params.cols; x += 1) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#a9d957' : '#9ccc4a';
        ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
      }
    }

    // Food (apple)
    ctx.fillStyle = '#e53935';
    ctx.beginPath();
    ctx.arc(ox + food.x * cell + cell / 2, oy + food.y * cell + cell / 2, Math.max(4, cell * 0.28), 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#2e7d32';
    ctx.fillRect(
      ox + food.x * cell + Math.floor(cell * 0.55),
      oy + food.y * cell + Math.floor(cell * 0.18),
      Math.max(2, Math.floor(cell * 0.12)),
      Math.max(3, Math.floor(cell * 0.25)),
    );

    // Snake
    snake.forEach((p, idx) => {
      const isHead = idx === snake.length - 1;
      ctx.fillStyle = isHead ? '#1e40af' : '#2563eb';
      ctx.fillRect(ox + p.x * cell + 1, oy + p.y * cell + 1, cell - 2, cell - 2);
      if (isHead) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(ox + p.x * cell + Math.floor(cell * 0.6), oy + p.y * cell + Math.floor(cell * 0.3), 3, 3);
      }
    });

    // HUD
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = "800 12px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = 'left';
    ctx.fillText(`Score: ${score}`, 12, 18);

    if (!alive) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.font = "900 18px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText('Game Over', width / 2, height / 2 - 10);
      ctx.font = "700 12px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText('Click to restart', width / 2, height / 2 + 12);
    }
  };

  const jump = () => {
    if (!running) return;
    if (!alive) {
      reset();
      return;
    }
  };

  const step = (ts: number) => {
    if (!running) return;
    if (!lastTs) lastTs = ts;
    const delta = ts - lastTs;
    lastTs = ts;
    acc += delta;
    while (acc >= params.tickMs) {
      acc -= params.tickMs;
      tick();
    }
    draw();
    rafId = window.requestAnimationFrame(step);
  };

  const setDirection = (x: number, y: number) => {
    if (!alive) return;
    if (x === -dir.x && y === -dir.y) return;
    nextDir = { x, y };
  };

  return {
    start: () => {
      if (running) return;
      running = true;
      reset();
      lastTs = 0;
      acc = 0;
      rafId = window.requestAnimationFrame(step);
    },
    stop: () => {
      running = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = 0;
    },
    jump,
    getScore: () => score,
    isRunning: () => running,
    handleKeyDown: (event) => {
      const key = event.key.toLowerCase();
      if (key === 'arrowup' || key === 'w') {
        setDirection(0, -1);
        return true;
      }
      if (key === 'arrowdown' || key === 's') {
        setDirection(0, 1);
        return true;
      }
      if (key === 'arrowleft' || key === 'a') {
        setDirection(-1, 0);
        return true;
      }
      if (key === 'arrowright' || key === 'd') {
        setDirection(1, 0);
        return true;
      }
      return false;
    },
  };
};

const resolveEngineFactory = (game: AssistantArcadeGame) => {
  if (game === 'flappy') return createFlappyEngine;
  if (game === 'block') return createBlockRunnerEngine;
  if (game === 'dino') return createDinoPixelEngine;
  return createSnakeEngine;
};

export default function AssistantCanvasGames({
  game,
  difficulty,
  onRequestFullscreen,
  showFullscreenButton = true,
  canvasHeight = 210,
  className,
}: AssistantCanvasGamesProps) {
  const { t } = useTranslation();
  const { ref, size } = useElementSize<HTMLDivElement>();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<Engine | null>(null);
  const [score, setScore] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const engineFactory = useMemo(() => resolveEngineFactory(game), [game]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width <= 0 || size.height <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(size.width * dpr));
    canvas.height = Math.max(1, Math.floor(size.height * dpr));
    setIsReady(true);
  }, [size.height, size.width]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isReady) return;

    engineRef.current?.stop();
    engineRef.current = engineFactory(canvas, difficulty, setScore);
    engineRef.current.start();

    const handleKeyDown = (event: KeyboardEvent) => {
      const engine = engineRef.current;
      if (!engine) return;
      if (engine.handleKeyDown) {
        const consumed = engine.handleKeyDown(event);
        if (consumed) {
          event.preventDefault();
          return;
        }
      }
      if (event.code !== 'Space') return;
      event.preventDefault();
      engine.jump();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      engineRef.current?.stop();
      engineRef.current = null;
    };
  }, [difficulty, engineFactory, isReady]);

  return (
    <div className={`rounded-3xl border border-orange-100 bg-white p-3 shadow-sm ${className ?? ''}`}>
      <div className="flex items-center justify-between gap-2 pb-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-extrabold text-slate-900">
            {game === 'flappy'
              ? t('assistant.games.options.flappy', { defaultValue: 'Flappy Book' })
              : game === 'block'
                ? t('assistant.games.options.block', { defaultValue: 'Block Jump' })
                : game === 'dino'
                  ? t('assistant.games.options.dino', { defaultValue: 'Dino Run' })
                  : t('assistant.games.options.snake', { defaultValue: 'Snake' })}
          </p>
          <p className="text-[11px] font-semibold text-slate-500">Score: {score}</p>
        </div>
        {showFullscreenButton && onRequestFullscreen ? (
          <button
            type="button"
            onClick={onRequestFullscreen}
            className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-extrabold text-orange-700 transition hover:bg-orange-600 hover:text-white"
            aria-label="Open fullscreen"
          >
            <Maximize2 className="h-4 w-4" />
            {t('assistant.games.fullscreen', { defaultValue: 'Fullscreen' })}
          </button>
        ) : null}
      </div>
      <div
        ref={ref}
        className="relative overflow-hidden rounded-2xl border border-orange-200 bg-slate-950"
        style={{ height: canvasHeight }}
      >
        <canvas
          ref={canvasRef}
          className="h-full w-full cursor-pointer"
          onClick={() => engineRef.current?.jump()}
          aria-label="Arcade game canvas"
        />
        {!isReady ? (
          <div className="absolute inset-0 grid place-items-center text-xs font-semibold text-white/80">
            {t('common.loading', { defaultValue: 'Loading...' })}
          </div>
        ) : null}
      </div>
      <p className="mt-2 text-center text-[11px] font-semibold text-slate-500">
        {game === 'snake'
          ? t('assistant.games.snakeHelp', { defaultValue: 'Use Arrow keys / WASD. Click to restart.' })
          : t('assistant.games.arcadeHelp', { defaultValue: 'Click / Tap (or Space) to play.' })}
      </p>
    </div>
  );
}
